import type {GoogleInfo} from "@mytour/shared";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import axios from "axios";
import {toast} from "react-toastify";
import {useIsAuthenticated} from "./authentication/authenticationQueries";

const keysStore = {
  root: ["google"] as const,
  googleInfo: () => [...keysStore.root, "googleInfo"] as const,
};
export function useGoogleInfo() {
  const {isAuthenticated} = useIsAuthenticated();
  const {
    data: googleInfo,
    isLoading,
    isFetching,
    refetch,
    error,
  } = useQuery({
    queryKey: keysStore.googleInfo(),
    queryFn: googleApiService.getGoogleInfo,
    enabled: isAuthenticated,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    googleInfo,
    isSessionLoading: isLoading,
    isSessionFetching: isFetching,
    sessionError: error,
    refetch,
  };
}

export function useGoogleAuth() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending, error, isSuccess, isError} = useMutation({
    async mutationFn(code: string) {
      await googleApiService.getGoogleAuth(code);
      await queryClient.invalidateQueries({queryKey: keysStore.root});
    },
  });

  return {
    authenticateWithGoogle: mutateAsync,
    isAuthenticating: isPending,
    authError: error,
    authSuccess: isSuccess,
    authFailure: isError,
  };
}

export function useRevokeGoogle() {
  const queryClient = useQueryClient();
  const {mutateAsync, isPending, error, isSuccess, isError} = useMutation({
    async mutationFn() {
      await googleApiService.revoke();
      await queryClient.invalidateQueries({queryKey: keysStore.root});
    },
    onError(e: any) {
      if (e.response?.status === 406) {
        toast.error("Merci d'accepter l'accès à l'agenda Google.");
      } else {
        toast.error("Impossible de se connecter à l'agenda.");
        if (e.message) {
          toast.error(e.message);
        }
      }
    },
  });

  return {
    revokeGoogle: mutateAsync,
    isRevoking: isPending,
    revokeError: error,
    revokeSuccess: isSuccess,
    revokeFailure: isError,
  };
}

const googleApiService = {
  async getGoogleInfo(): Promise<GoogleInfo> {
    const response = await axios.get("api/google");
    return response.data;
  },
  async getGoogleAuth(code: string): Promise<void> {
    await axios.post(`api/google/auth?code=${code}`);
  },
  async revoke(): Promise<void> {
    await axios.post("api/google/revoke/");
  },
};
